import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Share2 } from "lucide-react";
import { toast } from "sonner";
import GradientBar from "@/components/common/GradientBar";
import ErrorInline from "@/components/common/ErrorInline";
import MarkdownPreview from "@/components/common/MarkdownPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SectionCard from "./SectionCard";
import MissingDocsCallout from "./MissingDocsCallout";
import UploadDropzone from "./UploadDropzone";
import RecentUploads from "./RecentUploads";
import {
  createDoc,
  deleteDoc,
  draftWithAi,
  downloadDoc,
  getVault,
  uploadDoc,
} from "@/lib/vaultApi";
import { createShare } from "@/lib/shareApi";

/** Phase F: real Venture Vault with share-link, fuzzy match, inline preview. */
export default function VaultPage() {
  const [vault, setVault] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyDocIds, setBusyDocIds] = useState(() => new Set());
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewMd, setPreviewMd] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await getVault();
      setVault(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Couldn't load vault";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const adjustedSections = useMemo(() => {
    if (!vault) return [];
    return vault.sections.map((s) => {
      const docs = vault.docs.filter((d) => d.section_id === s.id);
      const total = docs.length;
      const missing = docs.filter((d) => d.status === "missing").length;
      const status = total === 0
        ? "missing"
        : missing === 0
          ? "ready"
          : missing === total
            ? "missing"
            : "incomplete";
      return {
        ...s,
        docs,
        status,
        lastUpdated: docs[0]?.updated_at?.slice(0, 10) ?? "—",
        updatedBy: docs.find((d) => d.updated_by)?.updated_by ?? null,
      };
    });
  }, [vault]);

  const totalDocs = vault?.readiness?.total ?? 0;
  const completeDocs = vault?.readiness?.complete ?? 0;
  const readinessPercent = vault?.readiness?.percent ?? 0;

  const missingItems = useMemo(() => {
    if (!vault) return [];
    const out = [];
    for (const s of vault.sections) {
      for (const d of vault.docs) {
        if (d.section_id === s.id && d.status === "missing") {
          out.push({ ...d, sectionName: s.name, sectionId: s.id });
        }
      }
    }
    return out;
  }, [vault]);

  const setBusy = (id, on) => {
    setBusyDocIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // ---- Upload (top button + dropzone) — Phase F: fuzzy-match into existing
  // missing slot before creating a new doc row. ----
  const handleFiles = async (fileList, sectionIdOverride) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    for (const file of files) {
      const tmpId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((prev) => [
        { id: tmpId, name: file.name, size: formatBytes(file.size), progress: 0 },
        ...prev,
      ]);
      try {
        let targetDocId = null;
        if (!sectionIdOverride && vault) {
          const matched = fuzzyMatchMissingDoc(file.name, vault.docs || []);
          if (matched) targetDocId = matched.id;
        }
        if (!targetDocId) {
          const sid = sectionIdOverride || guessSectionFor(file.name);
          const ext = (file.name.split(".").pop() || "doc").toLowerCase();
          const kind = inferKind(ext);
          const created = await createDoc({
            section_id: sid,
            name: file.name,
            kind,
          });
          targetDocId = created.id;
        }
        await uploadDoc(targetDocId, file, (p) => {
          setUploads((prev) =>
            prev.map((x) => (x.id === tmpId ? { ...x, progress: p } : x)),
          );
        });
        setUploads((prev) =>
          prev.map((x) => (x.id === tmpId ? { ...x, progress: 100 } : x)),
        );
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`, {
          description: err?.response?.data?.detail || err.message,
        });
        setUploads((prev) => prev.filter((x) => x.id !== tmpId));
      }
    }
    refresh();
  };

  // ---- Replace existing missing doc (Add file on row) ----
  const handleAddFileToDoc = async (doc, file) => {
    if (!file) return;
    const tmpId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setUploads((prev) => [
      { id: tmpId, name: file.name, size: formatBytes(file.size), progress: 0 },
      ...prev,
    ]);
    setBusy(doc.id, true);
    try {
      await uploadDoc(doc.id, file, (p) => {
        setUploads((prev) =>
          prev.map((x) => (x.id === tmpId ? { ...x, progress: p } : x)),
        );
      });
      setUploads((prev) =>
        prev.map((x) => (x.id === tmpId ? { ...x, progress: 100 } : x)),
      );
    } catch (err) {
      toast.error(`Upload failed: ${file.name}`, {
        description: err?.response?.data?.detail || err.message,
      });
      setUploads((prev) => prev.filter((x) => x.id !== tmpId));
    } finally {
      setBusy(doc.id, false);
      refresh();
    }
  };

  // ---- Draft with AI ----
  const handleDraftWithAi = async (doc) => {
    setBusy(doc.id, true);
    try {
      await draftWithAi(doc.id);
      toast.success("Draft ready", {
        description: `${doc.name} drafted by Vault AI.`,
      });
      refresh();
    } catch (err) {
      toast.error("Draft failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(doc.id, false);
    }
  };

  // ---- Download (complete docs) and Preview (drafts) ----
  const handleDownload = async (doc) => {
    try {
      const resp = await downloadDoc(doc.id);
      const blob = resp.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error("Download failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const handlePreview = async (doc) => {
    // For markdown / text drafts, render inline in a dialog.
    if (doc.ai_generated || doc.kind === "text") {
      setPreviewDoc(doc);
      setPreviewLoading(true);
      try {
        const resp = await downloadDoc(doc.id);
        const text = await resp.data.text();
        setPreviewMd(text);
      } catch (err) {
        setPreviewMd(
          `# Couldn't load preview\n\n${err?.response?.data?.detail || err.message || ""}`,
        );
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    // Otherwise fall back to download.
    handleDownload(doc);
  };

  // ---- Delete ----
  const handleDelete = async (doc) => {
    setBusy(doc.id, true);
    try {
      await deleteDoc(doc.id);
      toast.success(`Removed ${doc.name}`);
      refresh();
    } catch (err) {
      toast.error("Delete failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(doc.id, false);
    }
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const { url, expires_at } = await createShare();
      // Resolve to absolute origin if backend returned a relative url.
      const absoluteUrl = url.startsWith("http")
        ? url
        : `${window.location.origin}${url}`;
      try {
        await navigator.clipboard?.writeText(absoluteUrl);
        toast.success("Read-only link copied", {
          description: `Expires ${new Date(expires_at).toLocaleDateString()}.`,
        });
      } catch {
        toast.success("Read-only link ready", {
          description: absoluteUrl,
        });
      }
    } catch (err) {
      toast.error("Couldn't create share link", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <div data-testid="vault-page" className="px-5 pb-16 pt-6 sm:px-8 sm:pt-8 lg:px-10">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end md:gap-6"
      >
        <div className="min-w-0">
          <div className="eyebrow">Data Room</div>
          <h1 className="mt-2 text-[40px] font-extrabold leading-[1] tracking-tight text-zinc-900 sm:text-[56px] lg:text-[72px]">
            Venture Vault
          </h1>
          <p
            data-testid="vault-subtitle"
            className="mt-2 text-[13px] text-zinc-500 sm:text-[14px]"
          >
            Your investor-ready company file ·{" "}
            <span className="font-bold text-zinc-700">
              {completeDocs} of {totalDocs} documents complete
            </span>
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-shrink-0 md:gap-3">
          <div
            data-testid="vault-readiness"
            className="card-soft inline-flex items-center gap-3 rounded-full px-4 py-2"
          >
            <span className="text-[12.5px] font-semibold text-zinc-800">
              Readiness {readinessPercent}%
            </span>
            <div className="w-20 sm:w-24">
              <GradientBar
                value={readinessPercent}
                testId="vault-readiness-bar"
                height={5}
              />
            </div>
          </div>
          <button
            type="button"
            data-testid="vault-share-btn"
            onClick={handleShare}
            disabled={sharing}
            className="inline-flex h-10 min-h-[40px] items-center gap-1.5 rounded-full border border-black/[0.06] bg-white px-4 text-[12.5px] font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 disabled:opacity-60 md:h-9"
          >
            <Share2 className="h-3.5 w-3.5" strokeWidth={2.2} />
            <span className="hidden sm:inline">{sharing ? "Creating link…" : "Share data room"}</span>
            <span className="sm:hidden">{sharing ? "…" : "Share"}</span>
          </button>
          <label
            data-testid="vault-upload-btn"
            className="ml-auto inline-flex h-10 min-h-[40px] cursor-pointer items-center gap-1.5 rounded-full bg-black px-4 text-[12.5px] font-semibold text-white transition-colors hover:bg-zinc-800 md:ml-0 md:h-9"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.6} />
            Upload
            <input
              type="file"
              multiple
              className="hidden"
              data-testid="vault-upload-input-top"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>
        </div>
      </motion.div>

      {loading && !vault ? (
        <div
          data-testid="vault-loading"
          className="mt-8 card-soft rounded-[28px] p-10 text-center text-[13px] text-zinc-500"
        >
          Loading vault…
        </div>
      ) : loadError ? (
        <div className="mt-8">
          <ErrorInline message={loadError} onRetry={refresh} />
        </div>
      ) : (
        <>
          {/* 2x2 grid → 1-col on mobile */}
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {adjustedSections.map((s) => (
              <SectionCard
                key={s.id}
                section={s}
                busyDocIds={busyDocIds}
                onAddFile={handleAddFileToDoc}
                onDraftWithAi={handleDraftWithAi}
                onDownload={handlePreview}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Missing callout */}
          <div className="mt-7">
            <MissingDocsCallout items={missingItems} />
          </div>

          {/* Recent uploads + Dropzone */}
          <div className="mt-7 space-y-4">
            <RecentUploads uploads={uploads} />
            <UploadDropzone onFiles={handleFiles} />
          </div>
        </>
      )}

      {/* Inline markdown preview for drafts */}
      <Dialog
        open={Boolean(previewDoc)}
        onOpenChange={(o) => {
          if (!o) {
            setPreviewDoc(null);
            setPreviewMd("");
          }
        }}
      >
        <DialogContent
          data-testid="preview-dialog"
          className="max-h-[80vh] max-w-3xl overflow-hidden p-0"
        >
          <DialogHeader className="border-b border-black/[0.06] bg-white px-6 py-4">
            <DialogTitle className="text-[15px] font-extrabold tracking-tight text-zinc-900">
              {previewDoc?.name ?? "Preview"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto bg-white px-6 py-5">
            {previewLoading ? (
              <div className="text-center text-[12.5px] text-zinc-500">Loading…</div>
            ) : (
              <MarkdownPreview markdown={previewMd} />
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] bg-zinc-50/80 px-6 py-3">
            <button
              type="button"
              data-testid="preview-download"
              onClick={() => previewDoc && handleDownload(previewDoc)}
              className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-3 py-1.5 text-[11.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              Download
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes == null || bytes === 0) return "—";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function inferKind(ext) {
  const e = ext.toLowerCase();
  if (e === "pdf") return "pdf";
  if (["doc", "docx", "txt", "md"].includes(e)) return "doc";
  if (["xls", "xlsx", "csv"].includes(e)) return "sheet";
  if (["png", "jpg", "jpeg", "gif", "zip"].includes(e)) return "image";
  if (["mp4", "mov"].includes(e)) return "video";
  return "doc";
}

function guessSectionFor(filename) {
  const n = filename.toLowerCase();
  if (/(deck|pitch|one[- ]?pager|demo)/.test(n)) return "deck";
  if (/(financial|model|cap[- ]?table|burn|runway|pricing|unit|economics|faq)/.test(n)) return "financials";
  if (/(legal|incorp|bylaw|stock|msa|privacy|tos|dpa|ip|certificate)/.test(n)) return "legal";
  return "product";
}

// ---- Phase F: fuzzy-match an upload filename against existing missing docs.
// Strip extension/punctuation, tokenize, count overlapping tokens.
// Match if 2+ tokens overlap OR one name contains the other (loose).
function normalizeForMatch(s) {
  return (s || "")
    .toLowerCase()
    .replace(/\.[a-z0-9]{1,5}$/, "") // strip ext
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const STOPWORDS = new Set([
  "the", "and", "of", "v1", "v2", "v3", "v4", "final", "draft", "template", "memo", "doc", "docs",
]);

function fuzzyMatchMissingDoc(filename, allDocs) {
  const targets = (allDocs || []).filter((d) => d.status === "missing");
  if (targets.length === 0) return null;
  const fileNorm = normalizeForMatch(filename);
  if (!fileNorm) return null;
  const fileTokens = fileNorm
    .split(" ")
    .filter((t) => t.length >= 3 && !STOPWORDS.has(t));
  if (fileTokens.length === 0) return null;

  let best = null;
  let bestScore = 0;
  for (const d of targets) {
    const docNorm = normalizeForMatch(d.name);
    const docTokens = docNorm
      .split(" ")
      .filter((t) => t.length >= 3 && !STOPWORDS.has(t));
    if (docTokens.length === 0) continue;
    const docSet = new Set(docTokens);
    const overlap = fileTokens.filter((t) => docSet.has(t)).length;
    let score = overlap;
    // Substring containment bumps the score so e.g. "investor_faq.docx"
    // beats a 1-token match against "Pitch deck v3.pdf".
    if (docNorm.includes(fileNorm) || fileNorm.includes(docNorm)) {
      score += 3;
    }
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  // Require either ≥2 overlapping tokens or substring containment.
  return bestScore >= 2 ? best : null;
}
